import React, { useState, useMemo, useCallback } from "react";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { Badge } from "../components/ui/badge";
import {
  Triangle,
  Calculator,
  Info,
  BookOpen,
  Lightbulb,
  AlertCircle,
  ArrowRight,
  Copy,
  Check,
  RotateCcw,
  CheckCircle2,
  XCircle,
  Equal,
  Scale,
  Sigma,
} from "lucide-react";

const PI = Math.PI;
const DEGREES_TO_RADIANS = PI / 180;

// Helper to evaluate trig expressions
const evalTrig = (expr, angleRad) => {
  try {
    const sin = Math.sin(angleRad);
    const cos = Math.cos(angleRad);
    const tan = Math.tan(angleRad);
    const cot = 1 / tan;
    const sec = 1 / cos;
    const csc = 1 / sin;
    
    // Replace trig functions in expression
    let result = expr
      .replace(/sin²|sin\^2/g, `(${sin}*${sin})`)
      .replace(/cos²|cos\^2/g, `(${cos}*${cos})`)
      .replace(/tan²|tan\^2/g, `(${tan}*${tan})`)
      .replace(/cot²|cot\^2/g, `(${cot}*${cot})`)
      .replace(/sec²|sec\^2/g, `(${sec}*${sec})`)
      .replace(/csc²|csc\^2/g, `(${csc}*${csc})`)
      .replace(/sin/g, `(${sin})`)
      .replace(/cos/g, `(${cos})`)
      .replace(/tan/g, `(${tan})`)
      .replace(/cot/g, `(${cot})`)
      .replace(/sec/g, `(${sec})`)
      .replace(/csc/g, `(${csc})`);
    
    // eslint-disable-next-line no-eval
    return eval(result);
  } catch {
    return NaN;
  }
};

// Copy Button Component
const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={handleCopy}>
      {copied ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
    </Button>
  );
};

// Identity Categories
const IDENTITY_CATEGORIES = {
  pythagorean: {
    name: 'Pythagorean',
    color: 'emerald',
    identities: [
      { left: 'sin² + cos²', right: '1', name: 'Basic Pythagorean', explanation: 'The most fundamental identity. On the unit circle, sin and cos represent the y and x coordinates, and x² + y² = 1 for any point on a unit circle.' },
      { left: '1 + tan²', right: 'sec²', name: 'Tangent Pythagorean', explanation: 'Derived by dividing sin² + cos² = 1 by cos² on both sides.' },
      { left: '1 + cot²', right: 'csc²', name: 'Cotangent Pythagorean', explanation: 'Derived by dividing sin² + cos² = 1 by sin² on both sides.' },
    ],
  },
  reciprocal: {
    name: 'Reciprocal',
    color: 'blue',
    identities: [
      { left: 'csc', right: '1/sin', name: 'Cosecant', explanation: 'Cosecant is the reciprocal of sine.' },
      { left: 'sec', right: '1/cos', name: 'Secant', explanation: 'Secant is the reciprocal of cosine.' },
      { left: 'cot', right: '1/tan', name: 'Cotangent', explanation: 'Cotangent is the reciprocal of tangent, or cos/sin.' },
      { left: 'tan', right: 'sin/cos', name: 'Tangent Ratio', explanation: 'Tangent is defined as the ratio of sine to cosine.' },
    ],
  },
  cofunction: {
    name: 'Co-function',
    color: 'amber',
    identities: [
      { left: 'sin(90° - θ)', right: 'cos(θ)', name: 'Sine Co-function', explanation: 'The sine of the complement equals the cosine. This is why they are called co-functions.' },
      { left: 'cos(90° - θ)', right: 'sin(θ)', name: 'Cosine Co-function', explanation: 'The cosine of the complement equals the sine.' },
      { left: 'tan(90° - θ)', right: 'cot(θ)', name: 'Tangent Co-function', explanation: 'The tangent of the complement equals the cotangent.' },
    ],
  },
  evenOdd: {
    name: 'Even/Odd',
    color: 'rose',
    identities: [
      { left: 'sin(-θ)', right: '-sin(θ)', name: 'Sine is Odd', explanation: 'Sine is an odd function: reflecting across the y-axis negates the value.' },
      { left: 'cos(-θ)', right: 'cos(θ)', name: 'Cosine is Even', explanation: 'Cosine is an even function: symmetric about the y-axis.' },
      { left: 'tan(-θ)', right: '-tan(θ)', name: 'Tangent is Odd', explanation: 'Tangent is an odd function, like sine.' },
    ],
  },
  doubleAngle: {
    name: 'Double Angle',
    color: 'violet',
    identities: [
      { left: 'sin(2θ)', right: '2sin(θ)cos(θ)', name: 'Double Angle Sine', explanation: 'The sine of double an angle equals twice the product of sine and cosine.' },
      { left: 'cos(2θ)', right: 'cos²(θ) - sin²(θ)', name: 'Double Angle Cosine', explanation: 'One form of the double angle formula for cosine. Can also be written as 2cos²-1 or 1-2sin².' },
      { left: 'tan(2θ)', right: '2tan(θ)/(1-tan²(θ))', name: 'Double Angle Tangent', explanation: 'The double angle formula for tangent.' },
    ],
  },
  sumDifference: {
    name: 'Sum/Difference',
    color: 'cyan',
    identities: [
      { left: 'sin(A+B)', right: 'sinA·cosB + cosA·sinB', name: 'Sine Sum', explanation: 'The sine of a sum is not the sum of the sines. This formula is essential for deriving other identities.' },
      { left: 'sin(A-B)', right: 'sinA·cosB - cosA·sinB', name: 'Sine Difference', explanation: 'Similar to sine sum, but with subtraction.' },
      { left: 'cos(A+B)', right: 'cosA·cosB - sinA·sinB', name: 'Cosine Sum', explanation: 'Note the subtraction sign - this is the opposite of what many expect.' },
      { left: 'cos(A-B)', right: 'cosA·cosB + sinA·sinB', name: 'Cosine Difference', explanation: 'Uses addition, the opposite of cosine sum.' },
    ],
  },
};

// Verification visualization
const VerificationCircle = ({ isVerified, leftValue, rightValue }) => {
  const diff = Math.abs(leftValue - rightValue);
  const percentage = isVerified ? 100 : Math.max(0, 100 - diff * 100);
  const circumference = 2 * PI * 40;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  
  return (
    <svg viewBox="0 0 100 100" className="w-32 h-32 mx-auto">
      <circle
        cx="50"
        cy="50"
        r="40"
        fill="none"
        stroke="hsl(var(--muted))"
        strokeWidth="8"
      />
      <circle
        cx="50"
        cy="50"
        r="40"
        fill="none"
        stroke={isVerified ? '#22c55e' : '#ef4444'}
        strokeWidth="8"
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={strokeDashoffset}
        transform="rotate(-90 50 50)"
        className="transition-all duration-500"
      />
      {isVerified ? (
        <CheckCircle2 x="30" y="30" width="40" height="40" className="text-green-500" />
      ) : (
        <XCircle x="30" y="30" width="40" height="40" className="text-red-500" />
      )}
    </svg>
  );
};

function TrigIdentityCalculator() {
  const [activeCategory, setActiveCategory] = useState('pythagorean');
  const [angle, setAngle] = useState(30);
  const [unit, setUnit] = useState('degrees');
  const [customLeft, setCustomLeft] = useState('sin² + cos²');
  const [customRight, setCustomRight] = useState('1');
  const [selectedIdentity, setSelectedIdentity] = useState(0);
  const [expandedSteps, setExpandedSteps] = useState(['step-1']);

  const angleRad = useMemo(() => {
    return unit === 'degrees' ? angle * DEGREES_TO_RADIANS : angle;
  }, [angle, unit]);

  const category = IDENTITY_CATEGORIES[activeCategory];
  const identity = category.identities[selectedIdentity];

  // Trig values for display
  const trigValues = useMemo(() => {
    const rad = angleRad;
    return {
      sin: Math.sin(rad),
      cos: Math.cos(rad),
      tan: Math.tan(rad),
      csc: 1 / Math.sin(rad),
      sec: 1 / Math.cos(rad),
      cot: 1 / Math.tan(rad),
    };
  }, [angleRad]);

  // Evaluate custom expressions
  const customVerification = useMemo(() => {
    const leftVal = evalTrig(customLeft, angleRad);
    const rightVal = evalTrig(customRight, angleRad);
    const diff = Math.abs(leftVal - rightVal);
    return {
      leftValue: leftVal,
      rightValue: rightVal,
      isVerified: isFinite(leftVal) && isFinite(rightVal) && diff < 0.0001,
      difference: diff,
    };
  }, [customLeft, customRight, angleRad]);

  const handleReset = useCallback(() => {
    setAngle(30);
    setUnit('degrees');
    setCustomLeft('sin² + cos²');
    setCustomRight('1');
  }, []);

  const formatValue = (val) => {
    if (!isFinite(val)) return 'undefined';
    return val.toFixed(6);
  };

  const applyIdentity = useCallback((id) => {
    setCustomLeft(id.left);
    setCustomRight(id.right);
  }, []);

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Page Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center">
            <Triangle className="w-6 h-6 text-amber-500" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Trigonometric Identity Verifier</h1>
            <p className="text-muted-foreground">Verify and explore trig identities with step-by-step proofs</p>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left Column - Calculator Panel */}
        <div className="lg:sticky lg:top-24 lg:self-start">
          <Card className="rounded-2xl p-6 border bg-card">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <Calculator className="w-5 h-5 text-primary" />
                <span className="font-semibold text-foreground">Verifier</span>
              </div>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleReset} title="Reset">
                <RotateCcw className="w-4 h-4" />
              </Button>
            </div>

            {/* Custom Identity Input */}
            <div className="space-y-4">
              <div>
                <Label className="text-sm text-muted-foreground mb-2 block">Left Side Expression</Label>
                <Input
                  type="text"
                  value={customLeft}
                  onChange={(e) => setCustomLeft(e.target.value)}
                  className="border font-mono h-12"
                  placeholder="sin² + cos²"
                />
              </div>
              <div className="flex justify-center">
                <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                  <Equal className="w-5 h-5 text-muted-foreground" />
                </div>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground mb-2 block">Right Side Expression</Label>
                <Input
                  type="text"
                  value={customRight}
                  onChange={(e) => setCustomRight(e.target.value)}
                  className="border font-mono h-12"
                  placeholder="1"
                />
              </div>
            </div>

            {/* Test Angle */}
            <div className="mt-6 space-y-4">
              <div>
                <Label className="text-sm text-muted-foreground mb-2 block">Test Angle (θ)</Label>
                <Input
                  type="number"
                  value={angle}
                  onChange={(e) => setAngle(parseFloat(e.target.value) || 0)}
                  className="border text-lg font-mono h-12"
                />
              </div>
              <Tabs value={unit} onValueChange={setUnit}>
                <TabsList className="w-full">
                  <TabsTrigger value="degrees" className="flex-1">Degrees (°)</TabsTrigger>
                  <TabsTrigger value="radians" className="flex-1">Radians</TabsTrigger>
                </TabsList>
              </Tabs>

              {/* Quick Angles */}
              <div>
                <Label className="text-xs text-muted-foreground mb-2 block">Quick Test Angles</Label>
                <div className="flex flex-wrap gap-2">
                  {[0, 30, 45, 60, 90, 120, 180].map((a) => (
                    <Button
                      key={a}
                      variant="outline"
                      size="sm"
                      className={`text-xs rounded-lg ${angle === a && unit === 'degrees' ? 'bg-primary text-primary-foreground border-primary' : ''}`}
                      onClick={() => { setAngle(a); setUnit('degrees'); }}
                    >
                      {a}°
                    </Button>
                  ))}
                </div>
              </div>
            </div>

            {/* Verification Result */}
            <div className="mt-6 pt-4 border-t border-border">
              <Label className="text-sm font-medium text-foreground mb-3 block">Verification Result</Label>
              <div className={`rounded-xl p-4 border ${customVerification.isVerified ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
                <div className="flex items-center justify-between mb-3">
                  <span className={`font-bold ${customVerification.isVerified ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                    {customVerification.isVerified ? 'Identity Verified!' : 'Not Equal'}
                  </span>
                  {customVerification.isVerified ? (
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-500" />
                  )}
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Left:</span>
                    <span className="font-mono text-foreground">{formatValue(customVerification.leftValue)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Right:</span>
                    <span className="font-mono text-foreground">{formatValue(customVerification.rightValue)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Difference:</span>
                    <span className="font-mono text-foreground">{customVerification.difference.toExponential(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Center/Right Column - Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Identity Categories */}
          <Card className="rounded-2xl p-6 border bg-card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-foreground flex items-center gap-2">
                <Sigma className="w-4 h-4 text-primary" />
                Identity Categories
                <Badge variant="secondary" className="ml-2 text-xs">Click to Apply</Badge>
              </h2>
            </div>

            {/* Category Tabs */}
            <div className="flex flex-wrap gap-2 mb-6">
              {Object.entries(IDENTITY_CATEGORIES).map(([key, cat]) => (
                <Button
                  key={key}
                  variant={activeCategory === key ? 'default' : 'outline'}
                  size="sm"
                  className="text-xs"
                  onClick={() => { setActiveCategory(key); setSelectedIdentity(0); }}
                >
                  {cat.name}
                </Button>
              ))}
            </div>

            {/* Identity Cards */}
            <div className="grid sm:grid-cols-2 gap-3">
              {category.identities.map((id, idx) => (
                <div
                  key={idx}
                  className={`rounded-xl p-4 border cursor-pointer transition-all hover:shadow-md ${
                    selectedIdentity === idx 
                      ? `ring-2 ring-${category.color}-500 bg-${category.color}-500/5` 
                      : 'bg-muted/30 hover:bg-muted/50'
                  }`}
                  onClick={() => { setSelectedIdentity(idx); applyIdentity(id); }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-muted-foreground font-medium">{id.name}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 text-xs"
                      onClick={(e) => { e.stopPropagation(); applyIdentity(id); }}
                    >
                      Apply
                    </Button>
                  </div>
                  <div className="font-mono text-sm text-foreground">
                    <span className="text-primary">{id.left}</span>
                    <span className="mx-2">=</span>
                    <span className="text-primary">{id.right}</span>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Visualization */}
          <Card className="rounded-2xl p-6 border bg-card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-foreground flex items-center gap-2">
                <Scale className="w-4 h-4 text-primary" />
                Visual Verification
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Verification Circle */}
              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-3">Verification Status</h3>
                <div className="rounded-xl p-6 bg-muted/30 border flex flex-col items-center">
                  <VerificationCircle
                    isVerified={customVerification.isVerified}
                    leftValue={customVerification.leftValue}
                    rightValue={customVerification.rightValue}
                  />
                  <p className={`mt-4 font-semibold text-lg ${customVerification.isVerified ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                    {customVerification.isVerified ? 'True Identity' : 'Not an Identity'}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">at θ = {angle}{unit === 'degrees' ? '°' : ' rad'}</p>
                </div>
              </div>

              {/* Trig Values */}
              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-3">Current Trig Values</h3>
                <div className="rounded-xl p-4 bg-muted/30 border space-y-2">
                  {Object.entries(trigValues).map(([func, value]) => (
                    <div key={func} className="flex items-center justify-between bg-background rounded-lg p-2">
                      <span className="text-sm text-muted-foreground font-mono">{func}({angle}{unit === 'degrees' ? '°' : ''}) =</span>
                      <div className="flex items-center gap-1">
                        <span className="font-mono text-sm text-foreground">{formatValue(value)}</span>
                        <CopyButton text={formatValue(value)} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Identity Explanation */}
            {identity && (
              <div className="mt-6 p-4 bg-primary/5 rounded-xl border border-primary/20">
                <h4 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                  <Lightbulb className="w-4 h-4 text-primary" />
                  {identity.name}
                </h4>
                <p className="text-muted-foreground">{identity.explanation}</p>
              </div>
            )}
          </Card>

          {/* Step-by-Step Proof */}
          <Card className="rounded-2xl p-6 border bg-card">
            <h2 className="font-semibold text-foreground flex items-center gap-2 mb-4">
              <BookOpen className="w-4 h-4 text-primary" />
              Step-by-Step Verification
            </h2>

            <Accordion type="multiple" value={expandedSteps} onValueChange={setExpandedSteps}>
              <AccordionItem value="step-1" className="border-b border-border">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold">1</span>
                    <span className="font-medium">Identify the expressions</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="pl-11 space-y-3">
                    <p className="text-muted-foreground">We need to verify if these two expressions are equal:</p>
                    <div className="bg-secondary/50 rounded-lg p-4 space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="text-muted-foreground">Left side:</span>
                        <code className="font-mono font-semibold text-foreground bg-secondary px-2 py-0.5 rounded">{customLeft}</code>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-muted-foreground">Right side:</span>
                        <code className="font-mono font-semibold text-foreground bg-secondary px-2 py-0.5 rounded">{customRight}</code>
                      </div>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="step-2" className="border-b border-border">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold">2</span>
                    <span className="font-medium">Substitute test angle</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="pl-11 space-y-4">
                    <p className="text-muted-foreground">
                      Testing with θ = <span className="font-mono text-foreground font-semibold">{angle}{unit === 'degrees' ? '°' : ' rad'}</span>
                    </p>
                    <div className="bg-secondary/50 rounded-lg p-4 space-y-2">
                      <p className="text-sm">At this angle:</p>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>sin(θ) = <span className="font-mono">{trigValues.sin.toFixed(4)}</span></div>
                        <div>cos(θ) = <span className="font-mono">{trigValues.cos.toFixed(4)}</span></div>
                        <div>tan(θ) = <span className="font-mono">{formatValue(trigValues.tan)}</span></div>
                        <div>cot(θ) = <span className="font-mono">{formatValue(trigValues.cot)}</span></div>
                      </div>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="step-3" className="border-0">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold">3</span>
                    <span className="font-medium">Evaluate and compare</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="pl-11 space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="bg-secondary/50 rounded-lg p-4">
                        <span className="text-xs text-muted-foreground block mb-2">Left side evaluates to:</span>
                        <code className="font-mono text-lg font-bold text-foreground">{formatValue(customVerification.leftValue)}</code>
                      </div>
                      <div className="bg-secondary/50 rounded-lg p-4">
                        <span className="text-xs text-muted-foreground block mb-2">Right side evaluates to:</span>
                        <code className="font-mono text-lg font-bold text-foreground">{formatValue(customVerification.rightValue)}</code>
                      </div>
                    </div>
                    
                    <div className={`rounded-lg p-4 ${customVerification.isVerified ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
                      {customVerification.isVerified ? (
                        <p className="text-green-600 dark:text-green-400 font-semibold flex items-center gap-2">
                          <CheckCircle2 className="w-5 h-5" />
                          The expressions are equal! Identity verified at θ = {angle}{unit === 'degrees' ? '°' : ' rad'}.
                        </p>
                      ) : (
                        <p className="text-red-600 dark:text-red-400 font-semibold flex items-center gap-2">
                          <XCircle className="w-5 h-5" />
                          The expressions are not equal. Difference: {customVerification.difference.toExponential(2)}
                        </p>
                      )}
                    </div>
                    
                    <p className="text-sm text-muted-foreground">
                      <strong>Note:</strong> A true identity holds for all valid angles. Test with multiple angles to increase confidence.
                    </p>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </Card>

          {/* Understanding Identities */}
          <Card className="rounded-2xl p-6 border bg-card">
            <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-primary" />
              Understanding Trigonometric Identities
            </h2>
            
            <div className="space-y-6">
              <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
                <h3 className="font-bold text-lg text-emerald-600 dark:text-emerald-400 mb-2">What is a Trigonometric Identity?</h3>
                <p className="text-muted-foreground">
                  A trigonometric identity is an equation involving trig functions that is <strong>true for ALL valid angle values</strong>. Unlike regular equations that are true for specific values, identities are universally true.
                </p>
              </div>

              <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-500/20">
                <h3 className="font-bold text-lg text-blue-600 dark:text-blue-400 mb-2">Why Are Identities Important?</h3>
                <ul className="text-muted-foreground space-y-2">
                  <li>• <strong>Simplification:</strong> Convert complex expressions into simpler ones</li>
                  <li>• <strong>Solving equations:</strong> Transform difficult equations into solvable forms</li>
                  <li>• <strong>Calculus:</strong> Essential for integration and differentiation</li>
                  <li>• <strong>Physics:</strong> Used in wave equations, oscillations, and signal processing</li>
                </ul>
              </div>

              <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20">
                <h3 className="font-bold text-lg text-amber-600 dark:text-amber-400 mb-2">How to Use This Verifier</h3>
                <ol className="text-muted-foreground space-y-2">
                  <li>1. Enter expressions using: sin, cos, tan, cot, sec, csc</li>
                  <li>2. Use ² or ^2 for squared functions (e.g., sin² or sin^2)</li>
                  <li>3. Use standard math operators: +, -, *, /, (, )</li>
                  <li>4. Test with multiple angles to verify true identities</li>
                </ol>
              </div>
            </div>
          </Card>

          {/* Common Mistakes */}
          <Card className="rounded-2xl p-6 bg-destructive/5 border border-destructive/20">
            <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-destructive" />
              Common Mistakes to Avoid
            </h2>
            <ul className="space-y-3">
              {[
                'Thinking sin(A+B) = sin(A) + sin(B) - This is FALSE! Use sum formulas.',
                'Confusing identities with equations - Identities must be true for ALL angles.',
                'Forgetting domain restrictions - Some identities are undefined at certain angles (e.g., tan at 90°).',
                'Mixing up sin² notation - sin²(x) means (sin(x))², NOT sin(x²).',
                'Testing only one angle - A single verification doesn\'t prove an identity.',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-destructive mt-2 flex-shrink-0" />
                  <span className="text-muted-foreground">{item}</span>
                </li>
              ))}
            </ul>
          </Card>

          {/* Reference Table */}
          <Card className="rounded-2xl p-6 border bg-card">
            <h2 className="text-xl font-bold text-foreground mb-4">Quick Reference - All Identities</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2">Category</th>
                    <th className="text-left py-2">Identity</th>
                  </tr>
                </thead>
                <tbody className="text-muted-foreground">
                  {Object.values(IDENTITY_CATEGORIES).flatMap(cat => 
                    cat.identities.map((id, idx) => (
                      <tr key={`${cat.name}-${idx}`} className="border-b">
                        <td className="py-2">{idx === 0 ? cat.name : ''}</td>
                        <td className="py-2 font-mono">{id.left} = {id.right}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card>

          {/* FAQ */}
          <Card className="rounded-2xl p-6 border bg-card">
            <h2 className="text-xl font-bold text-foreground mb-4">Frequently Asked Questions</h2>
            <Accordion type="single" collapsible className="w-full">
              {[
                {
                  q: 'How do I know if something is a true identity?',
                  a: 'A true identity holds for all valid angles. Test with multiple diverse angles (0°, 30°, 45°, 90°, etc.). If it works for all, it\'s likely an identity. For mathematical proof, you need algebraic manipulation.'
                },
                {
                  q: 'What\'s the difference between sin² and sin^2?',
                  a: 'They mean the same thing: (sin(θ))². Both notations are supported in this calculator. sin²θ is standard mathematical notation.'
                },
                {
                  q: 'Why does verification fail at certain angles?',
                  a: 'Some expressions are undefined at certain angles. For example, tan(90°) and sec(90°) are undefined because cos(90°) = 0. This doesn\'t mean the identity is false - it just has domain restrictions.'
                },
                {
                  q: 'Can I verify my own expressions?',
                  a: 'Yes! Enter any expression using sin, cos, tan, cot, sec, csc with standard math operators. Use ² for squared terms. The calculator will evaluate both sides and compare.'
                },
              ].map((faq, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-b border-border">
                  <AccordionTrigger className="text-left hover:no-underline">
                    <span className="font-medium text-foreground">{faq.q}</span>
                  </AccordionTrigger>
                  <AccordionContent>
                    <p className="text-muted-foreground leading-relaxed">{faq.a}</p>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default TrigIdentityCalculator;
